import React from 'react'
import { Field, useFormikContext } from 'formik'
import { CenterApplicationFormType } from '../../../types'

export const CenterDocumentationForm: React.FC = () => {
  const { values } = useFormikContext<CenterApplicationFormType>()

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-2xl leading-6 font-bold text-[#3C2416]">
          Documentation, Protocols & Treatment Access
        </h3>
        <p className="mt-2 text-sm text-[#142841]">
          Please indicate which protocols, policies, and treatment options are
          available at your center.
        </p>
      </div>

      <div className="px-4 py-5 sm:px-6 space-y-8">
        {/* Written Protocols Section */}
        <div>
          <h4 className="text-lg font-semibold text-[#3C2416] mb-4">
            Written Protocols and Documentation
          </h4>
          <div className="space-y-4">
            <div className="flex items-center">
              <Field
                type="checkbox"
                name="writtenProcForSickleCellDisease"
                id="writtenProcForSickleCellDisease"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="writtenProcForSickleCellDisease"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Written protocol for acute and chronic sickle cell disease
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="emergencyDeptProtocolsForScd"
                id="emergencyDeptProtocolsForScd"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="emergencyDeptProtocolsForScd"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Emergency department protocols for sickle cell disease
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="trackingQualityMetricsProtocol"
                id="trackingQualityMetricsProtocol"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="trackingQualityMetricsProtocol"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Protocol for tracking quality metrics
              </label>
            </div>

            {/* Adult-specific protocols */}
            {(values.centerType === 'adult' ||
              values.centerType === 'whole_life') && (
              <div className="flex items-center">
                <Field
                  type="checkbox"
                  name="recNewlyTransToAdultProtocol"
                  id="recNewlyTransToAdultProtocol"
                  className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
                />
                <label
                  htmlFor="recNewlyTransToAdultProtocol"
                  className="ml-3 text-sm font-medium text-[#3C2416]"
                >
                  Protocol for receiving newly transitioned patients to adult
                  care
                </label>
              </div>
            )}

            {/* Pediatric-specific protocols */}
            {(values.centerType === 'pediatric' ||
              values.centerType === 'whole_life') && (
              <>
                <div className="flex items-center">
                  <Field
                    type="checkbox"
                    name="transToAdultProtocol"
                    id="transToAdultProtocol"
                    className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
                  />
                  <label
                    htmlFor="transToAdultProtocol"
                    className="ml-3 text-sm font-medium text-[#3C2416]"
                  >
                    Protocol for transition to adult care
                  </label>
                </div>

                <div className="flex items-center">
                  <Field
                    type="checkbox"
                    name="trackingNewbornScreenProtocol"
                    id="trackingNewbornScreenProtocol"
                    className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
                  />
                  <label
                    htmlFor="trackingNewbornScreenProtocol"
                    className="ml-3 text-sm font-medium text-[#3C2416]"
                  >
                    Protocol for tracking newborn screening results
                  </label>
                </div>

                <div className="flex items-center">
                  <Field
                    type="checkbox"
                    name="pediatricHematology"
                    id="pediatricHematology"
                    className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
                  />
                  <label
                    htmlFor="pediatricHematology"
                    className="ml-3 text-sm font-medium text-[#3C2416]"
                  >
                    Pediatric hematology services
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Policies Section */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-lg font-semibold text-[#3C2416] mb-4">
            Center Policies
          </h4>
          <div className="space-y-4">
            <div className="flex items-center">
              <Field
                type="checkbox"
                name="opioidPolicy"
                id="opioidPolicy"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="opioidPolicy"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Opioid policy for pain management
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="infusionPolicy"
                id="infusionPolicy"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="infusionPolicy"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Infusion policy and procedures
              </label>
            </div>
          </div>
        </div>

        {/* Treatment Access Section */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-lg font-semibold text-[#3C2416] mb-4">
            Access to Treatments and Prescriptions
          </h4>
          <div className="space-y-4">
            <div className="flex items-center">
              <Field
                type="checkbox"
                name="accessToCompoundHydroxyurea"
                id="accessToCompoundHydroxyurea"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="accessToCompoundHydroxyurea"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Access to compound hydroxyurea
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="accessCrizanlizumabInfusions"
                id="accessCrizanlizumabInfusions"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="accessCrizanlizumabInfusions"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Access to crizanlizumab infusions
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="accessPrescribeVoxeletor"
                id="accessPrescribeVoxeletor"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="accessPrescribeVoxeletor"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Access to prescribe voxeletor
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="accessPharmaClinicalTrials"
                id="accessPharmaClinicalTrials"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="accessPharmaClinicalTrials"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Access to pharmaceutical clinical trials
              </label>
            </div>

            <div className="flex items-center">
              <Field
                type="checkbox"
                name="accessStemCellTransplant"
                id="accessStemCellTransplant"
                className="h-4 w-4 text-[#A6410A] focus:ring-[#A6410A] border-gray-300 rounded"
              />
              <label
                htmlFor="accessStemCellTransplant"
                className="ml-3 text-sm font-medium text-[#3C2416]"
              >
                Access to stem cell transplants
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
